import {
  addMarketMapKeys,
  setMarketMapKeys,
} from "@/features/auth/interactiveSurveySlice";
import type {
  MapKey,
  MapKeyPost,
  MapKeysBySurveyResponse,
} from "@/features/auth/types/auth.types";
import { createApi } from "@reduxjs/toolkit/query/react";
import { interactiveSurveyBaseQuery } from "./base";

// Survey Map Keys API - handles map key management
export const mapKeysApi = createApi({
  reducerPath: "mapKeysApi",
  baseQuery: interactiveSurveyBaseQuery,
  tagTypes: ["SurveyMapKeys"],
  endpoints: (build) => ({
    addSurveyMapKeys: build.mutation<MapKeysBySurveyResponse, MapKeyPost>({
      query: (body) => ({
        url: `/mapkeys`,
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          data.mapKeys.forEach((mapKey) => dispatch(addMarketMapKeys(mapKey)));
        } catch (error) {
          console.error("Error in addSurveyMapKeys:", error);
        }
      },
      invalidatesTags: (_result, _error, body) => [
        { type: "SurveyMapKeys", id: body.surveyId },
      ],
    }),
    updateSurveyMapKey: build.mutation<
      MapKey,
      { mapKeyId: string; surveyId: string; isShortlisted: boolean }
    >({
      query: ({ mapKeyId, isShortlisted }) => ({
        url: `/mapkeys/${mapKeyId}`,
        method: "PATCH",
        body: { isShortlisted },
      }),
      invalidatesTags: (_result, _error, body) => [
        { type: "SurveyMapKeys", id: body.surveyId },
      ],
    }),
    deleteSurveyMapKey: build.mutation<
      string,
      { mapKeyId: string; surveyId: string }
    >({
      query: ({ mapKeyId }) => ({
        url: `/mapkeys/${mapKeyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, body) => [
        { type: "SurveyMapKeys", id: body.surveyId },
      ],
    }),
    getSurveyMapKeysBySurvey: build.query<MapKeysBySurveyResponse, string>({
      query: (surveyId) => `/mapkeys/survey/${surveyId}`,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setMarketMapKeys(data.mapKeys));
        } catch (error) {
          console.error("Error in getSurveyMapKeysBySurvey:", error);
        }
      },
      providesTags: (_result, _error, surveyId) => [
        { type: "SurveyMapKeys", id: surveyId },
      ],
    }),
  }),
});

export const {
  useAddSurveyMapKeysMutation,
  useDeleteSurveyMapKeyMutation,
  useUpdateSurveyMapKeyMutation,
  useGetSurveyMapKeysBySurveyQuery,
} = mapKeysApi;
